/**
 * /requests — Unified role-adaptive request inbox, submit form, and detail view.
 *
 * What each role sees:
 *  - host / strata_admin / property_manager / system_admin:
 *      "Received" tab (requests assigned to them / their org)
 *      "Sent" tab (requests they submitted)
 *      "Submit New" to send a request upward
 *  - guest (no auth, accessed via guest token in GuestPortal) — handled separately
 *
 * Workflow:
 *   Submit → Acknowledge → In Progress → Resolved / Closed / Rejected
 *   Each status change triggers a notification to the other party.
 *   Comments are threaded below each request.
 */

import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Inbox, Send, Plus, ChevronDown, ChevronUp, MessageSquare,
  Clock, CheckCircle2, XCircle, AlertCircle, Loader2,
  Filter, RefreshCw, Wrench, Scale, CalendarDays, FileText, HelpCircle, ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import {
  useOrgRequests, useRequestDetail, useRequestComments,
  useCreateRequest, useUpdateRequestStatus, useAddRequestComment,
  REQUEST_TYPE_LABELS, REQUEST_TYPE_ICONS, REQUEST_STATUS_COLORS, PRIORITY_COLORS,
  type Request, type RequestType, type RequestStatus, type RequestRelationship, type RequestPriority,
} from "@/hooks/useRequests";
import { useWorkflowConfigs, getEnabledRequestTypes, RELATIONSHIP_LABELS } from "@/hooks/useWorkflowConfig";
import { useMyAssignments } from "@/hooks/useUserAssignments";
import type { UserRole } from "@/types/database";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(date: string) {
  const d = new Date(date);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const TYPE_ICONS: Record<RequestType, React.ElementType> = {
  maintenance: Wrench,
  dispute: Scale,
  amenity: CalendarDays,
  document: FileText,
  inquiry: HelpCircle,
  compliance: ShieldAlert,
};

const STATUS_STEP: Record<RequestStatus, number> = {
  new: 0, acknowledged: 1, in_progress: 2, pending_info: 2,
  resolved: 3, closed: 3, rejected: 3,
};

/** Which relationship to use when THIS role submits a request */
function getSubmitRelationship(role: UserRole): RequestRelationship {
  if (role === "host") return "host_to_pm";
  if (role === "property_manager") return "pm_to_strata";
  if (role === "strata_admin") return "strata_to_host";
  return "host_to_pm";
}

// ─── Request Row ─────────────────────────────────────────────────────────────

function RequestRow({
  request,
  onClick,
  isSelected,
}: {
  request: Request;
  onClick: () => void;
  isSelected: boolean;
}) {
  const TypeIcon = TYPE_ICONS[request.type] ?? HelpCircle;
  const submitterName =
    request.submitter?.full_name ?? request.submitted_by_name ?? "Guest";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left p-4 border-b last:border-b-0 transition-colors hover:bg-muted/40 ${
        isSelected ? "bg-primary/5 border-l-2 border-l-primary" : "border-l-2 border-l-transparent"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted shrink-0 mt-0.5">
          <TypeIcon className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold truncate">{request.subject}</span>
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${REQUEST_STATUS_COLORS[request.status]}`}>
              {request.status.replace(/_/g, " ")}
            </span>
            {(request.priority === "high" || request.priority === "urgent") && (
              <span className={`text-[10px] font-bold ${PRIORITY_COLORS[request.priority]}`}>
                ▲ {request.priority}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
            <span>{REQUEST_TYPE_ICONS[request.type]} {REQUEST_TYPE_LABELS[request.type]}</span>
            <span>·</span>
            <span>{submitterName}</span>
            {request.unit && <><span>·</span><span>Unit {request.unit.unit_number}</span></>}
          </div>
          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{request.description}</p>
        </div>
        <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(request.created_at)}</span>
      </div>
    </button>
  );
}

// ─── Request Detail Panel ─────────────────────────────────────────────────────

function RequestDetailPanel({
  requestId,
  onClose,
}: {
  requestId: string;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const { data: request, isLoading } = useRequestDetail(requestId);
  const { data: comments = [] } = useRequestComments(requestId);
  const updateStatus = useUpdateRequestStatus();
  const addComment = useAddRequestComment();

  const [comment, setComment] = useState("");
  const [internal, setInternal] = useState(false);
  const [resolution, setResolution] = useState("");
  const [newStatus, setNewStatus] = useState<RequestStatus | "">("");

  if (isLoading) return (
    <div className="p-6 space-y-4">
      <Skeleton className="h-6 w-2/3" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
  if (!request) return null;

  const isAuthority = user?.id === request.assigned_to_user_id ||
    ["strata_admin", "property_manager", "system_admin"].includes(user?.role ?? "");
  const isOpen = !["resolved", "closed", "rejected"].includes(request.status);

  const handleSendComment = () => {
    if (!comment.trim()) return;
    addComment.mutate({ requestId, body: comment.trim(), isInternal: internal });
    setComment("");
  };

  const handleUpdateStatus = () => {
    if (!newStatus) return;
    updateStatus.mutate({ id: requestId, status: newStatus, resolution_summary: resolution || undefined });
    setNewStatus("");
    setResolution("");
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-5 border-b">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold leading-tight">{request.subject}</h2>
            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground flex-wrap">
              <span>{REQUEST_TYPE_ICONS[request.type]} {REQUEST_TYPE_LABELS[request.type]}</span>
              <span>·</span>
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${REQUEST_STATUS_COLORS[request.status]}`}>
                {request.status.replace(/_/g, " ")}
              </span>
              <span>·</span>
              <span className={PRIORITY_COLORS[request.priority]}>{request.priority}</span>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>✕</Button>
        </div>

        {/* Meta */}
        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span>From: <strong className="text-foreground">
            {request.submitter?.full_name ?? request.submitted_by_name ?? "Guest"}
          </strong></span>
          <span>Submitted: <strong className="text-foreground">{timeAgo(request.created_at)}</strong></span>
          {request.assignee && (
            <span>Assigned to: <strong className="text-foreground">{request.assignee.full_name}</strong></span>
          )}
          {request.unit && (
            <span>Unit: <strong className="text-foreground">{request.unit.unit_number}</strong></span>
          )}
        </div>

        {/* Description */}
        <div className="mt-3 bg-muted/40 rounded-md p-3 text-sm whitespace-pre-wrap">
          {request.description}
        </div>

        {/* Resolution summary (if resolved) */}
        {request.resolution_summary && (
          <div className="mt-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-3 text-sm">
            <p className="text-xs font-semibold text-green-700 dark:text-green-400 mb-1">Resolution</p>
            <p className="text-sm">{request.resolution_summary}</p>
          </div>
        )}
      </div>

      {/* Comments thread */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {comments.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-8">
            No replies yet. Use the form below to respond.
          </p>
        )}
        {comments.map((c) => {
          const isMine = c.author_user_id === user?.id;
          return (
            <div key={c.id} className={`flex gap-2 ${isMine ? "flex-row-reverse" : ""}`}>
              <div className={`flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold shrink-0 ${
                isMine ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              }`}>
                {c.author_name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div className={`max-w-[80%] ${isMine ? "items-end" : ""} flex flex-col gap-0.5`}>
                <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                  {!isMine && <span className="font-medium">{c.author_name}</span>}
                  {c.is_internal && <Badge variant="outline" className="h-3.5 text-[9px] px-1">Internal</Badge>}
                  <span>{timeAgo(c.created_at)}</span>
                </div>
                <div className={`text-sm rounded-lg px-3 py-2 ${
                  isMine
                    ? "bg-primary text-primary-foreground"
                    : c.is_internal
                    ? "bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm"
                    : "bg-muted"
                }`}>
                  {c.body}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply + status update form */}
      {isOpen && (
        <div className="border-t p-4 space-y-3">
          {/* Status change (authority only) */}
          {isAuthority && (
            <div className="flex gap-2">
              <Select value={newStatus} onValueChange={(v) => setNewStatus(v as RequestStatus)}>
                <SelectTrigger className="h-8 text-xs flex-1">
                  <SelectValue placeholder="Update status…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="acknowledged">Acknowledged</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="pending_info">Needs More Info</SelectItem>
                  <SelectItem value="resolved">Mark Resolved</SelectItem>
                  <SelectItem value="rejected">Reject</SelectItem>
                  <SelectItem value="closed">Close</SelectItem>
                </SelectContent>
              </Select>
              {newStatus && (
                <Input
                  placeholder="Add resolution note (optional)"
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                  className="h-8 text-xs flex-[2]"
                />
              )}
              {newStatus && (
                <Button size="sm" className="h-8" onClick={handleUpdateStatus} disabled={updateStatus.isPending}>
                  {updateStatus.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Update"}
                </Button>
              )}
            </div>
          )}

          {/* Reply input */}
          <div className="space-y-2">
            <Textarea
              placeholder="Write a reply…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="min-h-[72px] resize-none text-sm"
            />
            <div className="flex items-center justify-between">
              {isAuthority && (
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={internal}
                    onChange={(e) => setInternal(e.target.checked)}
                    className="rounded"
                  />
                  Internal note (hidden from submitter)
                </label>
              )}
              <Button
                size="sm"
                className="ml-auto gap-1.5"
                onClick={handleSendComment}
                disabled={!comment.trim() || addComment.isPending}
              >
                {addComment.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                Reply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Submit New Request Sheet ─────────────────────────────────────────────────

function SubmitRequestSheet({
  open,
  onClose,
  relationship,
  availableTypes,
}: {
  open: boolean;
  onClose: () => void;
  relationship: RequestRelationship;
  availableTypes: RequestType[];
}) {
  const createRequest = useCreateRequest();
  const [type, setType] = useState<RequestType>(availableTypes[0] ?? "inquiry");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<RequestPriority>("normal");

  const handleSubmit = () => {
    if (!subject.trim() || !description.trim()) return;
    createRequest.mutate(
      { type, context_relationship: relationship, subject: subject.trim(), description: description.trim(), priority },
      {
        onSuccess: () => {
          setSubject(""); setDescription(""); setPriority("normal");
          onClose();
        },
      }
    );
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <Plus className="h-4 w-4" /> Submit a Request
          </SheetTitle>
          <SheetDescription className="text-xs">
            Sending to: <strong>{RELATIONSHIP_LABELS[relationship]}</strong>
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5">
          {/* Type */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">Request Type *</Label>
            <div className="grid grid-cols-2 gap-2">
              {availableTypes.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`rounded-lg border p-3 text-left transition-colors ${
                    type === t
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-muted/40"
                  }`}
                >
                  <div className="text-base">{REQUEST_TYPE_ICONS[t]}</div>
                  <div className="text-xs font-medium mt-1">{REQUEST_TYPE_LABELS[t]}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">Priority</Label>
            <Select value={priority} onValueChange={(v) => setPriority(v as RequestPriority)}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="high">⚠️ High</SelectItem>
                <SelectItem value="urgent">🚨 Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">Subject *</Label>
            <Input
              placeholder="Brief description of the issue"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">Details *</Label>
            <Textarea
              placeholder="Describe the issue in detail…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[120px] resize-none"
              maxLength={3000}
            />
            <p className="text-[10px] text-muted-foreground text-right">{description.length}/3000</p>
          </div>

          <Button
            className="w-full gap-2"
            onClick={handleSubmit}
            disabled={!subject.trim() || !description.trim() || createRequest.isPending}
          >
            {createRequest.isPending
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>
              : <><Send className="h-4 w-4" /> Submit Request</>
            }
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

function EmptyRequests({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Inbox className="h-10 w-10 text-muted-foreground/30 mb-3" />
      <p className="text-sm font-medium text-muted-foreground">No {label}</p>
      <p className="text-xs text-muted-foreground mt-1">
        {label.includes("received") ? "Requests submitted to you will appear here." : "Requests you submitted will appear here."}
      </p>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Requests() {
  const { user } = useAuth();
  const { data: assignments = [] } = useMyAssignments();
  const [searchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(searchParams.get("id"));
  const [composeOpen, setComposeOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState<RequestType | "all">("all");

  const role = (user?.role ?? "host") as UserRole;
  const relationship = getSubmitRelationship(role);

  // Fetch workflow configs (may be empty if migration not applied)
  const { data: wfConfigs = [] } = useWorkflowConfigs();
  const enabledTypes = getEnabledRequestTypes(wfConfigs, relationship);
  // Fall back to all types if configs not loaded yet
  const submitTypes: RequestType[] = enabledTypes.length > 0
    ? enabledTypes
    : ["maintenance", "dispute", "amenity", "document", "inquiry"];

  // Received (assigned to me or managed by me)
  const isManager = ["strata_admin", "property_manager", "system_admin"].includes(role);
  const { data: received = [], isLoading: recvLoading, refetch } = useOrgRequests(
    isManager ? undefined : { assignedToMe: true }
  );
  const { data: sent = [], isLoading: sentLoading } = useOrgRequests({ submittedByMe: true });

  const filterType = (list: Request[]) =>
    typeFilter === "all" ? list : list.filter((r) => r.type === typeFilter);

  const filteredReceived = filterType(received);
  const filteredSent = filterType(sent);

  useEffect(() => {
    const id = searchParams.get("id");
    if (id) setSelectedId(id);
  }, [searchParams]);

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden">
      {/* Left panel — list */}
      <div className={`flex flex-col w-full md:w-[420px] border-r shrink-0 ${selectedId ? "hidden md:flex" : "flex"}`}>
        {/* Header */}
        <div className="p-4 border-b space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold">Requests</h1>
              <p className="text-xs text-muted-foreground">
                {isManager ? "All requests in your organization" : "Your request inbox"}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => refetch()} className="h-8 w-8 p-0">
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" onClick={() => setComposeOpen(true)} className="h-8 gap-1.5">
                <Plus className="h-3.5 w-3.5" /> New
              </Button>
            </div>
          </div>

          {/* Type filter */}
          <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as RequestType | "all")}>
            <SelectTrigger className="h-8 text-xs">
              <Filter className="h-3.5 w-3.5 mr-1.5" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {(Object.keys(REQUEST_TYPE_LABELS) as RequestType[]).map((t) => (
                <SelectItem key={t} value={t}>
                  {REQUEST_TYPE_ICONS[t]} {REQUEST_TYPE_LABELS[t]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="received" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mx-4 mt-3 h-8 shrink-0">
            <TabsTrigger value="received" className="flex-1 text-xs gap-1">
              <Inbox className="h-3.5 w-3.5" /> Received
              {filteredReceived.filter(r => r.status === "new").length > 0 && (
                <Badge variant="destructive" className="h-4 min-w-[18px] px-1 text-[9px]">
                  {filteredReceived.filter(r => r.status === "new").length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="sent" className="flex-1 text-xs gap-1">
              <Send className="h-3.5 w-3.5" /> Sent
            </TabsTrigger>
          </TabsList>

          <TabsContent value="received" className="flex-1 overflow-y-auto mt-0 border-t">
            {recvLoading
              ? <div className="p-4 space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
              : filteredReceived.length === 0
              ? <EmptyRequests label="received requests" />
              : filteredReceived.map((r) => (
                  <RequestRow key={r.id} request={r} onClick={() => setSelectedId(r.id)} isSelected={selectedId === r.id} />
                ))
            }
          </TabsContent>

          <TabsContent value="sent" className="flex-1 overflow-y-auto mt-0 border-t">
            {sentLoading
              ? <div className="p-4 space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
              : filteredSent.length === 0
              ? <EmptyRequests label="sent requests" />
              : filteredSent.map((r) => (
                  <RequestRow key={r.id} request={r} onClick={() => setSelectedId(r.id)} isSelected={selectedId === r.id} />
                ))
            }
          </TabsContent>
        </Tabs>
      </div>

      {/* Right panel — detail */}
      <div className={`flex-1 overflow-y-auto ${!selectedId ? "hidden md:flex items-center justify-center" : "flex flex-col"}`}>
        {selectedId ? (
          <RequestDetailPanel requestId={selectedId} onClose={() => setSelectedId(null)} />
        ) : (
          <div className="text-center p-8">
            <MessageSquare className="h-12 w-12 text-muted-foreground/20 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Select a request to view details</p>
          </div>
        )}
      </div>

      {/* Submit sheet */}
      <SubmitRequestSheet
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        relationship={relationship}
        availableTypes={submitTypes}
      />
    </div>
  );
}
